import { useState, useEffect, useCallback, useMemo, useId } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import axios from 'axios';
import { linkGoogleAccount, unlinkGoogleAccount } from '@/services/authExtras';
import { useGoogleClientId } from '@/hooks/useGoogleClientId';
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';
import type { User, MfaStatusResponse } from '@/types/api';
import { formatMonthYear } from '@/i18n/intl';
import { labelFor } from '@/i18n/labels';
import { localizedApiErrorMessage } from '@/lib/utils';

export interface ProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function formatPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, '');
  const local = digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length === 0) return '';
  if (local.length <= 3) return `+1 (${local}`;
  if (local.length <= 6) return `+1 (${local.slice(0, 3)}) ${local.slice(3)}`;
  return `+1 (${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6, 10)}`;
}

function formatPhoneFromE164(e164: string | undefined): string {
  if (!e164) return '';
  const match = e164.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (match) return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
  return e164;
}

/** "Sep 2026" in en — the reader’s language through Intl (#2109 R3 L7). */
function formatMemberSince(isoDate: string): string {
  return formatMonthYear(isoDate, { month: 'short' });
}

// #2109 R3-B4: the account role word. An unknown role still renders
// capitalised, as it always did.
const ROLE_LABEL_KEY = {
  admin: 'profile:header.roles.admin',
  applicant: 'profile:header.roles.applicant',
  parent: 'profile:header.roles.parent',
  trfer: 'profile:header.roles.trfer',
} as const;

function capitalised(role: string): string {
  return `${role.charAt(0).toUpperCase()}${role.slice(1)}`;
}

function getInitials(user: User): string {
  const first = user.profile?.first_name?.[0] ?? '';
  const last = user.profile?.last_name?.[0] ?? '';
  if (first || last) return (first + last).toUpperCase();
  return user.email?.[0]?.toUpperCase() ?? 'U';
}

interface FormState {
  first_name: string;
  last_name: string;
  phone: string;
  personal_email: string;
  bio: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
}

const EMPTY_FORM: FormState = {
  first_name: '', last_name: '', phone: '', personal_email: '',
  bio: '', address: '', city: '', state: '', zip_code: '',
};

function profileToForm(user: User): FormState {
  return {
    first_name: user.profile?.first_name ?? '',
    last_name: user.profile?.last_name ?? '',
    phone: formatPhoneFromE164(user.profile?.phone),
    personal_email: user.profile?.personal_email ?? '',
    bio: user.profile?.bio ?? '',
    address: user.profile?.location?.address ?? '',
    city: user.profile?.location?.city ?? '',
    state: user.profile?.location?.state ?? '',
    zip_code: user.profile?.location?.zip_code ?? '',
  };
}

function formsEqual(a: FormState, b: FormState): boolean {
  return (
    a.first_name === b.first_name &&
    a.last_name === b.last_name &&
    a.phone === b.phone &&
    a.personal_email === b.personal_email &&
    a.bio === b.bio &&
    a.address === b.address &&
    a.city === b.city &&
    a.state === b.state &&
    a.zip_code === b.zip_code
  );
}

// ── MFA state machine ───────────────────────────────────────────────────────

type MfaUiState = 'idle' | 'picking-method' | 'awaiting-code' | 'disabling';

// ── Component ──────────────────────────────────────────────────────────────

export function ProfileDialog({ open, onOpenChange }: ProfileDialogProps) {
  const { t, i18n } = useTranslation(['profile', 'common']);
  const { toast } = useToast();
  // R3-B11: a server sentence reaches a toast only for an English reader;
  // every other reader gets this site's own localized line.
  const serverOr = (error: unknown, fallback: string) =>
    localizedApiErrorMessage(error, t, i18n.language) ?? fallback;
  const { logout, refresh } = useAuth();
  // #2422: one id prefix per mounted dialog, so every label names its input.
  const uid = useId();
  const fieldId = (name: string) => `${uid}-${name}`;

  const [profile, setProfile] = useState<User | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [passwordForm, setPasswordForm] = useState({ current: '', newPw: '', confirm: '' });
  const [isSaving, setIsSaving] = useState(false);

  // MFA state
  const [mfaStatus, setMfaStatus] = useState<MfaStatusResponse | null>(null);
  const [mfaUiState, setMfaUiState] = useState<MfaUiState>('idle');
  const [enrollMethod, setEnrollMethod] = useState<'email' | 'sms' | null>(null);
  const [enrollMasked, setEnrollMasked] = useState('');
  const [enrollCode, setEnrollCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);

  // Google account (#2324)
  const googleClientId = useGoogleClientId();
  const [googleBusy, setGoogleBusy] = useState(false);

  const loadProfile = useCallback(() => {
    api.auth.getCurrentUser()
      .then((r) => {
        if (r.status === 'success' && r.data?.user) {
          setProfile(r.data.user);
          setForm(profileToForm(r.data.user));
        } else {
          setLoadError(true);
        }
      })
      .catch(() => setLoadError(true));
  }, []);

  const loadMfaStatus = useCallback(() => {
    return api.mfa.status()
      .then((r) => {
        if (r.status === 'success' && r.data) setMfaStatus(r.data);
      })
      .catch(() => { /* non-fatal */ });
  }, []);

  // Reload profile + MFA status every time the dialog opens
  useEffect(() => {
    if (!open) return;
    setLoadError(false);
    setProfile(null);
    setForm(EMPTY_FORM);
    setPasswordForm({ current: '', newPw: '', confirm: '' });
    setMfaStatus(null);
    setMfaUiState('idle');
    setEnrollMethod(null);
    setEnrollMasked('');
    setEnrollCode('');
    setDisablePassword('');
    loadProfile();
    loadMfaStatus();
  }, [open, loadProfile, loadMfaStatus]);

  const baselineForm = useMemo(
    () => (profile ? profileToForm(profile) : EMPTY_FORM),
    [profile],
  );
  const profileDirty = useMemo(() => !formsEqual(form, baselineForm), [form, baselineForm]);
  const anyPasswordField = passwordForm.current || passwordForm.newPw || passwordForm.confirm;
  const isDirty = profileDirty || Boolean(anyPasswordField);

  const handleSave = async () => {
    const allPasswordFields = passwordForm.current && passwordForm.newPw && passwordForm.confirm;
    if (anyPasswordField && !allPasswordFields) {
      toast({
        variant: 'destructive',
        title: t('toasts.finishPassword'),
        description: t('toasts.finishPasswordDescription'),
      });
      return;
    }
    if (allPasswordFields && passwordForm.newPw !== passwordForm.confirm) {
      toast({ variant: 'destructive', title: t('toasts.passwordMismatch') });
      return;
    }

    setIsSaving(true);
    try {
      let profileSaved = false;
      if (profileDirty) {
        const r = await api.auth.updateProfile({
          first_name: form.first_name || undefined,
          last_name: form.last_name || undefined,
          phone: form.phone || undefined,
          personal_email: form.personal_email || undefined,
          bio: form.bio || undefined,
          location: {
            address: form.address || undefined,
            city: form.city || undefined,
            state: form.state || undefined,
            zip_code: form.zip_code || undefined,
          },
        });
        if (r.status !== 'success' || !r.data?.user) {
          toast({ variant: 'destructive', title: t('toasts.updateFailed'), description: t('toasts.notSaved') });
          return;
        }
        setProfile(r.data.user);
        setForm(profileToForm(r.data.user));
        profileSaved = true;
        // #2389: the shell's name button and the home greeting read the
        // session user, not this dialog. Re-read it so they show the new name
        // without a reload. The save already landed, so a failed re-read is
        // not an error the family needs to hear about.
        void refresh().catch(() => { /* the next /me picks it up */ });

        // If the server auto-disabled MFA because the phone changed, refresh status + toast
        if ((r as { mfa_auto_disabled?: boolean }).mfa_auto_disabled) {
          loadMfaStatus();
          toast({
            variant: 'destructive',
            title: t('toasts.mfaDisabled'),
            description: t('toasts.mfaAutoDisabledDescription'),
          });
        }
      }

      if (allPasswordFields) {
        try {
          await api.auth.changePassword(passwordForm.current, passwordForm.newPw);
          toast({
            title: profileSaved ? t('toasts.bothUpdated') : t('toasts.passwordUpdated'),
            description: t('toasts.loggingOut'),
          });
          setTimeout(() => logout(), 1500);
          return;
        } catch (pwErr: unknown) {
          const status = (pwErr as { response?: { status?: number } })?.response?.status;
          const msg =
            status === 401
              ? t('toasts.passwordWrong')
              : serverOr(pwErr, t('toasts.passwordNotChangedDescription'));
          toast({
            variant: 'destructive',
            title: profileSaved ? t('toasts.profileSavedPasswordNot') : t('toasts.passwordNotChanged'),
            description: msg,
          });
          return;
        }
      }

      toast({ title: t('toasts.profileUpdated') });
    } catch (e: unknown) {
      const msg = serverOr(e, t('toasts.notSaved'));
      toast({ variant: 'destructive', title: t('toasts.saveFailed'), description: msg });
    } finally {
      setIsSaving(false);
    }
  };

  // ── MFA handlers ──────────────────────────────────────────────────────────

  const handleEnrollStart = async (method: 'email' | 'sms') => {
    setMfaBusy(true);
    try {
      const r = await api.mfa.enrollStart(method);
      if (r.status === 'success' && r.data) {
        setEnrollMethod(method);
        setEnrollMasked(r.data.sent_to_masked);
        setEnrollCode('');
        setMfaUiState('awaiting-code');
      }
    } catch (e: unknown) {
      const msg = serverOr(e, t('toasts.codeNotSentDescription'));
      toast({ variant: 'destructive', title: t('toasts.codeNotSent'), description: msg });
    } finally {
      setMfaBusy(false);
    }
  };

  const handleEnrollVerify = async () => {
    if (!enrollMethod || enrollCode.length !== 6) return;
    setMfaBusy(true);
    try {
      const r = await api.mfa.enrollVerify(enrollMethod, enrollCode);
      if (r.status === 'success') {
        await loadMfaStatus();
        setMfaUiState('idle');
        setEnrollMethod(null);
        setEnrollCode('');
        toast({
          title: t('toasts.mfaEnabled'),
          description: enrollMethod === 'sms' ? t('toasts.codesToPhone') : t('toasts.codesToEmail'),
        });
      }
    } catch (e: unknown) {
      const msg = serverOr(e, t('toasts.codeInvalid'));
      toast({ variant: 'destructive', title: t('toasts.verifyFailed'), description: msg });
    } finally {
      setMfaBusy(false);
    }
  };

  const handleDisable = async () => {
    if (!disablePassword) return;
    setMfaBusy(true);
    try {
      const r = await api.mfa.disable(disablePassword);
      if (r.status === 'success') {
        await loadMfaStatus();
        setMfaUiState('idle');
        setDisablePassword('');
        toast({ title: t('toasts.mfaDisabled') });
      }
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      const msg = status === 401
        ? t('toasts.disableWrongPassword')
        : t('toasts.disableStillOn');
      toast({ variant: 'destructive', title: t('toasts.disableFailed'), description: msg });
    } finally {
      setMfaBusy(false);
    }
  };

  const handleGoogleLink = async (credential: string) => {
    setGoogleBusy(true);
    try {
      await linkGoogleAccount(credential);
      loadProfile();
      toast({ title: t('toasts.googleLinked') });
    } catch (e: unknown) {
      const code = axios.isAxiosError(e) ? (e.response?.data as { code?: string } | undefined)?.code : undefined;
      toast({
        variant: 'destructive',
        title: t('toasts.googleLinkFailed'),
        description: code === 'google_already_linked' ? t('google.alreadyLinked') : t('toasts.googleLinkFailedDescription'),
      });
    } finally {
      setGoogleBusy(false);
    }
  };

  const handleGoogleUnlink = async () => {
    setGoogleBusy(true);
    try {
      await unlinkGoogleAccount();
      loadProfile();
      toast({ title: t('toasts.googleUnlinked') });
    } catch {
      toast({ variant: 'destructive', title: t('toasts.googleUnlinkFailed'), description: t('toasts.googleLinkFailedDescription') });
    } finally {
      setGoogleBusy(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md flex flex-col max-h-[90vh]">

        {/* Fixed header */}
        <DialogHeader>
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 flex-shrink-0">
              <AvatarImage src={profile?.profile?.avatar_url ?? ''} />
              <AvatarFallback className="bg-primary text-primary-foreground text-card-title font-semibold">
                {profile ? getInitials(profile) : 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <DialogTitle className="text-card-title break-all">{profile?.email ?? ''}</DialogTitle>
              <DialogDescription className="text-caption">
                {profile
                  ? t('header.roleSince', {
                    role: labelFor(ROLE_LABEL_KEY, profile.role as keyof typeof ROLE_LABEL_KEY, t, capitalised(profile.role)),
                    date: formatMemberSince(profile.created_at),
                  })
                  : t('common:states.loading')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 min-h-0 -mx-6 px-6">
          {loadError ? (
            <div className="py-8 text-center">
              <p className="text-body text-stevens-ink-3 mb-3">{t('load.failed')}</p>
              <Button variant="outline" size="sm" onClick={() => { setLoadError(false); loadProfile(); }}>
                {t('load.retry')}
              </Button>
            </div>
          ) : !profile ? (
            <div className="py-8 text-center text-body text-stevens-ink-3">{t('common:states.loading')}</div>
          ) : (
            <>
              {/* ── Personal info ── */}
              <p className="text-caption font-medium uppercase tracking-wide text-stevens-ink-3 mt-2 mb-1">
                {t('sections.personalInfo')}
              </p>
              <div className="grid grid-cols-2 gap-2 py-2">
                <div>
                  <Label htmlFor={fieldId('first_name')} className="text-caption text-stevens-ink-3">{t('fields.firstName')}</Label>
                  <Input
                    id={fieldId('first_name')}
                    value={form.first_name}
                    onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                    maxLength={50}
                  />
                </div>
                <div>
                  <Label htmlFor={fieldId('last_name')} className="text-caption text-stevens-ink-3">{t('fields.lastName')}</Label>
                  <Input
                    id={fieldId('last_name')}
                    value={form.last_name}
                    onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                    maxLength={50}
                  />
                </div>
              </div>
              <div className="py-2">
                <Label htmlFor={fieldId('phone')} className="text-caption text-stevens-ink-3">{t('fields.phone')}</Label>
                <Input
                  id={fieldId('phone')}
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: formatPhoneInput(e.target.value) }))}
                  placeholder="+1 (555) 000-0000"
                  autoComplete="tel"
                />
              </div>
              <div className="py-2">
                <Label htmlFor={fieldId('alt_email')} className="text-caption text-stevens-ink-3">{t('fields.altEmail')}</Label>
                <Input
                  id={fieldId('alt_email')}
                  type="email"
                  value={form.personal_email}
                  onChange={(e) => setForm((f) => ({ ...f, personal_email: e.target.value }))}
                />
              </div>

              {/* ── Address ── */}
              <p className="text-caption font-medium uppercase tracking-wide text-stevens-ink-3 mt-4 mb-1">
                {t('sections.address')}
              </p>
              <div className="py-2">
                <Label htmlFor={fieldId('street')} className="text-caption text-stevens-ink-3">{t('fields.street')}</Label>
                <Input
                  id={fieldId('street')}
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  maxLength={100}
                  autoComplete="street-address"
                />
              </div>
              <div className="grid grid-cols-[1fr_80px_100px] gap-2 py-2">
                <div>
                  <Label htmlFor={fieldId('city')} className="text-caption text-stevens-ink-3">{t('fields.city')}</Label>
                  <Input
                    id={fieldId('city')}
                    value={form.city}
                    onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                    maxLength={100}
                    autoComplete="address-level2"
                  />
                </div>
                <div>
                  <Label htmlFor={fieldId('state')} className="text-caption text-stevens-ink-3">{t('fields.state')}</Label>
                  <Input
                    id={fieldId('state')}
                    value={form.state}
                    onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                    maxLength={100}
                    autoComplete="address-level1"
                  />
                </div>
                <div>
                  <Label htmlFor={fieldId('zip')} className="text-caption text-stevens-ink-3">{t('fields.zip')}</Label>
                  <Input
                    id={fieldId('zip')}
                    value={form.zip_code}
                    onChange={(e) => setForm((f) => ({ ...f, zip_code: e.target.value.slice(0, 20) }))}
                    maxLength={20}
                    autoComplete="postal-code"
                  />
                </div>
              </div>

              {/* ── Bio ── */}
              <p id={fieldId('bio_heading')} className="text-caption font-medium uppercase tracking-wide text-stevens-ink-3 mt-4 mb-1">
                {t('sections.bio')}
              </p>
              <div className="py-2">
                <Textarea
                  id={fieldId('bio')}
                  aria-labelledby={fieldId('bio_heading')}
                  value={form.bio}
                  onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                  rows={4}
                  maxLength={500}
                  placeholder={t('fields.bioPlaceholder')}
                />
                <p className="text-caption text-stevens-ink-3 text-right mt-1">
                  {form.bio.length}/500
                </p>
              </div>

              {/* ── Security (MFA) ── */}
              <div className="border-t mt-4 pt-4 pb-2">
                <p className="text-caption font-medium uppercase tracking-wide text-stevens-ink-3 mb-1">
                  {t('sections.mfa')}
                </p>

                {mfaStatus === null ? (
                  <p className="text-caption text-stevens-ink-3">{t('common:states.loading')}</p>
                ) : !mfaStatus.mfa_enabled && mfaUiState === 'idle' ? (
                  <>
                    <p className="text-caption text-stevens-ink-3 mb-3">
                      {t('mfa.offDescription')}
                    </p>
                    <Button size="sm" variant="outline" onClick={() => setMfaUiState('picking-method')}>
                      {t('mfa.enable')}
                    </Button>
                  </>
                ) : mfaStatus.mfa_enabled && mfaUiState === 'idle' ? (
                  <>
                    <p className="text-caption text-stevens-ink-3 mb-3">
                      <Trans
                        t={t}
                        i18nKey={mfaStatus.mfa_method === 'sms' ? 'mfa.onPhone' : 'mfa.onEmail'}
                        components={{ strong: <strong /> }}
                      />
                    </p>
                    <Button size="sm" variant="outline" onClick={() => { setDisablePassword(''); setMfaUiState('disabling'); }}>
                      {t('mfa.disable')}
                    </Button>
                  </>
                ) : mfaUiState === 'picking-method' ? (
                  <>
                    <p className="text-caption text-stevens-ink-3 mb-3">
                      {t('mfa.chooseMethod')}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={mfaBusy}
                        onClick={() => handleEnrollStart('email')}
                      >
                        {t('mfa.email')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={mfaBusy || !profile.profile?.phone}
                        title={!profile.profile?.phone ? t('mfa.addPhoneFirst') : undefined}
                        onClick={() => handleEnrollStart('sms')}
                      >
                        {t('mfa.text')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setMfaUiState('idle')}>
                        {t('common:actions.cancel')}
                      </Button>
                    </div>
                    {!profile.profile?.phone && (
                      <p className="text-caption text-stevens-ink-3 mt-2">
                        {t('mfa.addPhoneHint')}
                      </p>
                    )}
                  </>
                ) : mfaUiState === 'awaiting-code' ? (
                  <>
                    <p className="text-caption text-stevens-ink-3 mb-3">
                      <Trans
                        t={t}
                        i18nKey="mfa.sentTo"
                        values={{ destination: enrollMasked }}
                        components={{ strong: <strong /> }}
                      />
                    </p>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <Label htmlFor={fieldId('mfa_code')} className="text-caption text-stevens-ink-3">{t('mfa.codeLabel')}</Label>
                        <Input
                          id={fieldId('mfa_code')}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          placeholder="000000"
                          value={enrollCode}
                          onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          autoFocus
                        />
                      </div>
                      <Button
                        size="sm"
                        disabled={mfaBusy || enrollCode.length !== 6}
                        onClick={handleEnrollVerify}
                      >
                        {mfaBusy ? t('mfa.verifying') : t('mfa.verify')}
                      </Button>
                    </div>
                    <div className="flex gap-3 mt-2">
                      <button
                        type="button"
                        className="text-caption text-stevens-ink-3 hover:text-foreground transition-colors"
                        onClick={() => { setMfaUiState('picking-method'); setEnrollCode(''); }}
                      >
                        {t('mfa.differentMethod')}
                      </button>
                      <button
                        type="button"
                        className="text-caption text-stevens-ink-3 hover:text-foreground transition-colors"
                        onClick={() => { setMfaUiState('idle'); setEnrollCode(''); setEnrollMethod(null); }}
                      >
                        {t('common:actions.cancel')}
                      </button>
                    </div>
                  </>
                ) : mfaUiState === 'disabling' ? (
                  <>
                    <p className="text-caption text-stevens-ink-3 mb-3">
                      {t('mfa.disablePrompt')}
                    </p>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <Label htmlFor={fieldId('mfa_password')} className="text-caption text-stevens-ink-3">{t('mfa.currentPassword')}</Label>
                        <Input
                          id={fieldId('mfa_password')}
                          type="password"
                          value={disablePassword}
                          onChange={(e) => setDisablePassword(e.target.value)}
                          autoComplete="current-password"
                          autoFocus
                        />
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={mfaBusy || !disablePassword}
                        onClick={handleDisable}
                      >
                        {mfaBusy ? t('mfa.disabling') : t('mfa.disable')}
                      </Button>
                    </div>
                    <button
                      type="button"
                      className="text-caption text-stevens-ink-3 hover:text-foreground transition-colors mt-2"
                      onClick={() => { setMfaUiState('idle'); setDisablePassword(''); }}
                    >
                      {t('common:actions.cancel')}
                    </button>
                  </>
                ) : null}
              </div>

              {/* ── Google account (#2324) ── */}
              {(googleClientId || profile.google_link) && (
                <div className="border-t mt-4 pt-4 pb-2">
                  <p className="text-caption font-medium uppercase tracking-wide text-stevens-ink-3 mb-1">
                    {t('sections.google')}
                  </p>
                  {profile.google_link ? (
                    <>
                      <p className="text-caption text-stevens-ink-3 mb-3">
                        <Trans
                          t={t}
                          i18nKey="google.linkedAs"
                          values={{ email: profile.google_link.email }}
                          components={{ strong: <strong /> }}
                        />
                      </p>
                      <Button size="sm" variant="outline" disabled={googleBusy} onClick={handleGoogleUnlink}>
                        {googleBusy ? t('google.unlinking') : t('google.unlink')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-caption text-stevens-ink-3 mb-3">{t('google.notLinked')}</p>
                      {googleClientId && (
                        <GoogleSignInButton clientId={googleClientId} text="continue_with" onCredential={handleGoogleLink} />
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ── Change password — optional, leave blank to skip ── */}
              <div className="border-t mt-4 pt-4 pb-2">
                {/* Decoy username field: Chrome PM fills this instead of the ZIP input */}
                <input
                  type="text"
                  autoComplete="username"
                  aria-hidden="true"
                  tabIndex={-1}
                  readOnly
                  style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: 0, width: 0 }}
                />
                <p className="text-caption font-medium uppercase tracking-wide text-stevens-ink-3 mb-1">
                  {t('sections.changePassword')}
                </p>
                <p className="text-caption text-stevens-ink-3 mb-3">
                  {t('password.help')}
                </p>
                <div className="space-y-2">
                  <div>
                    <Label htmlFor={fieldId('pw_current')} className="text-caption text-stevens-ink-3">{t('password.current')}</Label>
                    <Input
                      id={fieldId('pw_current')}
                      type="password"
                      value={passwordForm.current}
                      onChange={(e) => setPasswordForm((f) => ({ ...f, current: e.target.value }))}
                      autoComplete="current-password"
                    />
                  </div>
                  <div>
                    <Label htmlFor={fieldId('pw_new')} className="text-caption text-stevens-ink-3">{t('password.new')}</Label>
                    <Input
                      id={fieldId('pw_new')}
                      type="password"
                      value={passwordForm.newPw}
                      onChange={(e) => setPasswordForm((f) => ({ ...f, newPw: e.target.value }))}
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <Label htmlFor={fieldId('pw_confirm')} className="text-caption text-stevens-ink-3">{t('password.confirm')}</Label>
                    <Input
                      id={fieldId('pw_confirm')}
                      type="password"
                      value={passwordForm.confirm}
                      onChange={(e) => setPasswordForm((f) => ({ ...f, confirm: e.target.value }))}
                      autoComplete="new-password"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Fixed footer — single Save handles profile + password atomically */}
        <div className="flex justify-end gap-2 pt-4 border-t mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            {t('common:actions.close')}
          </Button>
          <Button onClick={handleSave} disabled={!isDirty || isSaving || !profile || loadError}>
            {isSaving ? t('common:states.saving') : t('common:actions.saveChanges')}
          </Button>
        </div>

      </DialogContent>
    </Dialog>
  );
}
